/** The four product languages. Local, not imported: this package does not depend on @kilat/shared. */
export type GateLocale = 'en' | 'ms' | 'zh' | 'ta';

/**
 * The screen an app shows when the read it needs before ANYTHING can render —
 * which platform brand this is — failed or never answered.
 *
 * It renders above the i18n provider, because a failed brand read is the same
 * failure that leaves the translation bundle unloaded. So the copy ships
 * inline, in all four locales, picked from the device language. That is why
 * these are literals and why `ui-strings-ok` is on them.
 *
 * Styled only by tokens, so it carries no brand: it is safe in the white-label
 * customer app as well as the Kilat-branded ones.
 *
 * Before this existed, the dashboard, agent and customer apps logged the
 * failure and sat on their splash forever.
 */
export interface ConnectionGateCopy {
  readonly title: string;
  readonly body: string;
  readonly retry: string;
}

export const CONNECTION_GATE_COPY: Record<GateLocale, ConnectionGateCopy> = {
  en: {
    title: "We couldn't connect",
    body: 'Something interrupted the connection to our servers. Check your internet and try again.',
    retry: 'Try again',
  },
  ms: {
    title: 'Kami tidak dapat menyambung', // ui-strings-ok: pre-i18n gate copy
    body: 'Sambungan ke pelayan kami terganggu. Sila semak internet anda dan cuba lagi.', // ui-strings-ok: pre-i18n gate copy
    retry: 'Cuba lagi', // ui-strings-ok: pre-i18n gate copy
  },
  zh: {
    title: '无法连接', // ui-strings-ok: pre-i18n gate copy
    body: '与服务器的连接中断了。请检查您的网络连接后重试。', // ui-strings-ok: pre-i18n gate copy
    retry: '重试', // ui-strings-ok: pre-i18n gate copy
  },
  ta: {
    title: 'இணைக்க முடியவில்லை', // ui-strings-ok: pre-i18n gate copy
    body: 'எங்கள் சேவையகத்துடனான இணைப்பு தடைபட்டது. உங்கள் இணையத்தைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.', // ui-strings-ok: pre-i18n gate copy
    retry: 'மீண்டும் முயற்சிக்கவும்', // ui-strings-ok: pre-i18n gate copy
  },
};

/**
 * The language to speak before i18n has loaded: the person's own earlier pick
 * (`kilat.locale`, the key every app's switcher writes), else the device's.
 */
export function detectGateLocale(): GateLocale {
  try {
    const picked = window.localStorage.getItem('kilat.locale');
    if (picked === 'en' || picked === 'ms' || picked === 'zh' || picked === 'ta') return picked;
  } catch {
    /* storage blocked — fall through to the device language */
  }
  try {
    const tags =
      Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : [];
    for (const tag of tags) {
      const head2 = tag.toLowerCase().slice(0, 2);
      if (head2 === 'en') return 'en';
      if (head2 === 'ms' || head2 === 'id') return 'ms';
      if (head2 === 'zh') return 'zh';
      if (head2 === 'ta') return 'ta';
    }
  } catch {
    /* navigator unavailable — fall through to English */
  }
  return 'en';
}

export function ConnectionGateError({
  onRetry,
  copy,
}: {
  readonly onRetry: () => void;
  /** Per-app wording, e.g. find's "We couldn't load the directory". */
  readonly copy?: Record<GateLocale, ConnectionGateCopy>;
}) {
  const text = (copy ?? CONNECTION_GATE_COPY)[detectGateLocale()];
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-6)',
        background: 'var(--color-surface)',
        color: 'var(--color-on-surface)',
      }}
    >
      <div role="alert" style={{ maxWidth: 360, textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600, lineHeight: 1.3 }}>
          {text.title}
        </h1>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {text.body}
        </p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            marginTop: 'var(--space-6)',
            minHeight: 'var(--touch-target)',
            padding: '0.5em 1.5em',
            border: 'none',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            fontSize: 'var(--text-body)',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {text.retry}
        </button>
      </div>
    </main>
  );
}

/**
 * How long a first-paint read may go unanswered before the app says so.
 * Firestore never rejects an offline read on its own — it waits — so without
 * a deadline "the network is down" looks exactly like "still loading".
 */
export const CONNECTION_GATE_TIMEOUT_MS = 12_000;
